import React, { useMemo } from 'react';
import { BrowserRouter, Route, Routes, PathRouteProps, useLocation, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { appName, getBaseUrl, getRelativePath, getWebappEnv } from '@/service/utils/env';
import DataModelList from '@/pages/data-model/list';
import DataModelItem from '@/pages/data-model/item';
import PipelineList from '@/pages/pipeline/list';
import PipelineCreate, { IRefPipelineCreate } from '@/pages/pipeline/create';
import PipelineEdit, { IRefPipelineEdit } from '@/pages/pipeline/edit';
import { EPath, DYNAMIC_NAV_LIST, ENavLabel } from '@/service/utils/common';
import KeepAlive from '@/service/components/KeepAlive';
import { pathToRegexp } from 'path-to-regexp';
import { globalInfo } from '@/service/store';
import { ConfigProvider, Spin } from 'antd';
import NavWrapper from './service/components/NavWrapper';
import theme from './service/utils/localTheme';
import '@/service/style/antd-fix.less';
import './App.less';
import NoAuth from '@/service/components/NoAuth';
import { get } from 'lodash';

import { NavApp, initNavStore, PRODUCTS, useUser, ENVIRONMENTS } from '@pulse/nav';

// init nav package
const webappEnv = getWebappEnv();
initNavStore({ product: PRODUCTS.daas, env: webappEnv as ENVIRONMENTS });

ConfigProvider.config({
  prefixCls: 'daas-ant',
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
  },
});

export const routes: PathRouteProps[] = [
  {
    path: EPath.DATA_MODEL_LIST,
    element: <DataModelList />,
  },
  {
    path: EPath.DATA_MODEL_ITEM_WITH_MODEL_ID_ONLY,
    element: <DataModelItem />,
  },
  {
    path: EPath.DATA_MODEL_DETAIL,
    element: <DataModelItem />,
  },
  {
    path: EPath.PIPELINE_LIST,
    element: <PipelineList />,
  },
  {
    path: EPath.PIPELINE_CREATE,
    element: <PipelineCreate />,
  },
  {
    path: EPath.PIPELINE_EDIT,
    element: <PipelineEdit />,
  },
];
const routesRegList = routes.map((it) => pathToRegexp(it.path));

function handleBeforeUnload(evt: BeforeUnloadEvent) {
  evt.preventDefault();
  const href = window.location.pathname;
  const pathname = getRelativePath(href);
  const dynamicNav = DYNAMIC_NAV_LIST.find((it) => it.isActive({ pathname }));
  if (!dynamicNav) {
    return;
  }
  if ([ENavLabel.PIPELINE_CREATE, ENavLabel.PIPELINE_EDIT].indexOf(dynamicNav.label as ENavLabel) > -1) {
    evt.returnValue = 'A pipeline is in editing';
  }
}
window.onbeforeunload = handleBeforeUnload;
window.onunload = handleBeforeUnload;

const App: React.FC<{}> = () => {
  const { data, error, loading } = useUser();

  const hasPermission = useMemo(() => {
    if (error) {
      return false;
    }
    const role = get(data, ['user', 'role'], 'NA');

    return role === 'Admin';
  }, [data, error]);

  return (
    <div className="app">
      <NavApp />
      {loading ? (
        <div className="loading-wrapper">
          <Spin tip="Loading" size="large" />
        </div>
      ) : (
        <RoutesLayer hasPermission={hasPermission} />
      )}
    </div>
  );
};

const RoutesLayer = ({ hasPermission }: { hasPermission: boolean }) => {
  const filteredRoute = routes.filter(
    (it) => [EPath.PIPELINE_CREATE, EPath.PIPELINE_EDIT].indexOf(it.path as EPath) === -1,
  );

  return hasPermission ? (
    <div style={{ overflowY: 'scroll' }}>
      <NavWrapper />
      <Routes>
        {filteredRoute.map((it) => {
          return <Route {...it} key={it.path} />;
        })}
      </Routes>
      <AliveComponents />
      <DefaultPage />
    </div>
  ) : (
    <NoAuth appName={appName} />
  );
};

const AliveComponents: React.FC<{}> = () => {
  const location = useLocation();
  const navItem = DYNAMIC_NAV_LIST.find((it) => it.isActive(location));
  let Comp: React.ReactNode | null = null;
  const components: {
    [EPath.PIPELINE_CREATE]: React.ReactNode;
    [EPath.PIPELINE_EDIT]: React.ReactNode;
  } = useMemo(() => {
    return {
      [EPath.PIPELINE_CREATE]: (
        <PipelineCreate
          ref={(instance: IRefPipelineCreate) => {
            globalInfo.refComponentPipelineCreate = instance;
          }}
        />
      ),
      [EPath.PIPELINE_EDIT]: (
        <PipelineEdit
          ref={(instance: IRefPipelineEdit) => {
            globalInfo.refComponentPipelineEdit = instance;
          }}
        />
      ),
    };
  }, []);
  if (navItem) {
    Comp = components[navItem.path as keyof typeof components];
  }

  const activeName = navItem && Comp ? navItem.path : '';
  return (
    <KeepAlive activeName={activeName} include={DYNAMIC_NAV_LIST.map((it) => it.path)}>
      {Comp}
    </KeepAlive>
  );
};

function DefaultPage() {
  const { pathname } = useLocation();
  const matched = routesRegList.some((it) => it.test(pathname));
  if (matched) {
    return null;
  }
  return <Navigate to={EPath.DATA_MODEL_LIST} />;
}

const Entry: React.FC<{}> = () => {
  return (
    <React.StrictMode>
      <BrowserRouter basename={getBaseUrl()}>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} />
          <ConfigProvider prefixCls="daas-ant" theme={theme}>
            <App />
          </ConfigProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </React.StrictMode>
  );
};
export default Entry;
